import {
  approximateWallNormal,
  FloorTagged,
  SegmentHit,
  SpatialQueryGrid,
  VolumeHeightSample,
  VolumeLeafCallback,
} from '../SpatialQueryGrid';
import { BvhSpatialGrid, TerrainFallback } from './BvhSpatialGrid';
import { segmentHitBisect } from '../../navigation-mesh/SegmentHitBisect';

/**
 * Per-tile BVH builder injection point. Called by `TiledBvh.loadTile` for
 * each tile that enters the camera ring. The implementation is responsible
 * for:
 *   - fetching the tile archive (terrain + object placements)
 *   - resolving each placement's mesh template into a BLAS (via `BlasStore`)
 *   - building the TLAS and wrapping it in a `BvhSpatialGrid`
 *
 * Returning `null` means "no BVH coverage for this tile" — heightmap
 * fallback will serve any query against this tile, same as today's volgrid
 * path for tiles with no published volgrid.
 *
 * Kept as an injection point rather than wiring tile-archive + object-streamer
 * + model-registry internals directly into this class, so the streamer stays
 * decoupled from those subsystems' concrete types.
 */
export type TiledBvhGenerator = (tx: number, tz: number) => Promise<BvhSpatialGrid | null>;

/**
 * Camera-driven tiled BVH. Drop-in replacement for `TiledBvh` —
 * implements `SpatialQueryGrid`, owns a 3×3 ring of per-tile `BvhSpatialGrid`s,
 * applies terrain fallback at the tile-level for queries that miss the BVH.
 *
 * Mirrors `TiledBvh` lifecycle exactly:
 *   - `updateCamera(x, z)` each frame to refresh the ring
 *   - `loadInitialTiles(x, z)` blocks until the inner 3×3 is loaded
 *   - `setTerrainFallback(fn)` registers the smooth heightmap fallback
 *   - tiles evict on camera tile change
 */
export class TiledBvh implements SpatialQueryGrid {
  public voxelSize: number;

  private tilesX: number;
  private tilesZ: number;
  private tileWorldSize: number;
  private worldMinX: number;
  private worldMinZ: number;

  private loaded = new Map<string, BvhSpatialGrid>();
  private loading = new Set<string>();
  private lastCameraTileX = -999;
  private lastCameraTileZ = -999;

  private terrainFallback: TerrainFallback | null = null;
  private generator: TiledBvhGenerator;

  /**
   * Max in-flight tile loads. Capped to keep the worker queue focused on the
   * most recent ring during fast flight (matches `TiledBvh` rationale).
   */
  private static readonly MAX_INFLIGHT_LOADS = 3;

  constructor(
    tilesX: number,
    tilesZ: number,
    tileWorldSize: number,
    worldMinX: number,
    worldMinZ: number,
    voxelSize: number,
    generator: TiledBvhGenerator,
  ) {
    this.tilesX = tilesX;
    this.tilesZ = tilesZ;
    this.tileWorldSize = tileWorldSize;
    this.worldMinX = worldMinX;
    this.worldMinZ = worldMinZ;
    this.voxelSize = voxelSize;
    this.generator = generator;
  }

  setTerrainFallback(fn: TerrainFallback | null): void {
    this.terrainFallback = fn;
  }

  hasTileAt(x: number, z: number): boolean {
    const tx = Math.floor((x - this.worldMinX) / this.tileWorldSize);
    const tz = Math.floor((z - this.worldMinZ) / this.tileWorldSize);
    return this.loaded.has(`${tx},${tz}`);
  }

  /** Refresh the ring each frame. Mirrors `TiledBvh.updateCamera`. */
  updateCamera(x: number, z: number): void {
    const tx = Math.floor((x - this.worldMinX) / this.tileWorldSize);
    const tz = Math.floor((z - this.worldMinZ) / this.tileWorldSize);
    const tileChanged = tx !== this.lastCameraTileX || tz !== this.lastCameraTileZ;
    this.lastCameraTileX = tx;
    this.lastCameraTileZ = tz;

    const needed = new Set<string>();
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        const ntx = tx + dx;
        const ntz = tz + dz;
        if (ntx >= 0 && ntx < this.tilesX && ntz >= 0 && ntz < this.tilesZ) {
          needed.add(`${ntx},${ntz}`);
        }
      }
    }

    if (tileChanged) {
      for (const key of this.loaded.keys()) {
        if (!needed.has(key)) this.loaded.delete(key);
      }
    }

    for (const key of needed) {
      if (this.loading.size >= TiledBvh.MAX_INFLIGHT_LOADS) break;
      if (this.loaded.has(key) || this.loading.has(key)) continue;
      this.loadTile(key);
    }
  }

  async loadTileAt(tx: number, tz: number): Promise<void> {
    const key = `${tx},${tz}`;
    if (this.loaded.has(key) || this.loading.has(key)) return;
    await this.loadTile(key);
  }

  /** Load the initial 3×3 ring and wait for it. Mirrors `TiledBvh.loadInitialTiles`. */
  async loadInitialTiles(x: number, z: number): Promise<void> {
    const tx = Math.floor((x - this.worldMinX) / this.tileWorldSize);
    const tz = Math.floor((z - this.worldMinZ) / this.tileWorldSize);
    this.lastCameraTileX = tx;
    this.lastCameraTileZ = tz;
    const promises: Promise<void>[] = [];
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        const ntx = tx + dx;
        const ntz = tz + dz;
        if (ntx >= 0 && ntx < this.tilesX && ntz >= 0 && ntz < this.tilesZ) {
          promises.push(this.loadTileAt(ntx, ntz));
        }
      }
    }
    await Promise.all(promises);
  }

  private async loadTile(key: string): Promise<void> {
    this.loading.add(key);
    const [txStr, tzStr] = key.split(',');
    const tx = parseInt(txStr);
    const tz = parseInt(tzStr);
    try {
      const bvh = await this.generator(tx, tz);
      if (!bvh) return;
      // Drop stale results (camera flew past).
      const dx = Math.abs(tx - this.lastCameraTileX);
      const dz = Math.abs(tz - this.lastCameraTileZ);
      if (dx > 1 || dz > 1) return;
      this.loaded.set(key, bvh);
    } catch (e) {
      console.warn(`Failed to build BVH for tile (${tx},${tz}):`, e);
    } finally {
      this.loading.delete(key);
    }
  }

  private getTile(x: number, z: number): BvhSpatialGrid | undefined {
    const tx = Math.floor((x - this.worldMinX) / this.tileWorldSize);
    const tz = Math.floor((z - this.worldMinZ) / this.tileWorldSize);
    return this.loaded.get(`${tx},${tz}`);
  }

  // --- SpatialQueryGrid: delegate to per-tile BVH, apply fallback at tile level ---

  /**
   * Apply terrain fallback. Same shape as `TiledBvh.resolveFloorY`:
   * substitute the smooth heightmap Y when the tile result is missing OR
   * when it's tagged as terrain-origin.
   */
  private resolveFloorY(tile: FloorTagged | null | undefined, x: number, z: number): number | null {
    if (tile == null) return this.terrainFallback?.(x, z)?.height ?? null;
    if (!tile.terrainOrigin) return tile.y;
    const fb = this.terrainFallback?.(x, z);
    return fb?.height ?? tile.y;
  }

  isSolidAt(x: number, y: number, z: number): boolean {
    return this.getTile(x, z)?.isSolidAt(x, y, z) ?? false;
  }

  getFloorHeight(x: number, y: number, z: number): number | null {
    const tile = this.getTile(x, z)?.getFloorHeightTagged(x, y, z);
    return this.resolveFloorY(tile, x, z);
  }

  getFloorHeightTagged(x: number, y: number, z: number): FloorTagged | null {
    return this.getTile(x, z)?.getFloorHeightTagged(x, y, z) ?? null;
  }

  getCeilingHeight(x: number, y: number, z: number): number | null {
    return this.getTile(x, z)?.getCeilingHeight(x, y, z) ?? null;
  }

  getCeilingHeightAbove(x: number, y: number, z: number): number | null {
    return this.getTile(x, z)?.getCeilingHeightAbove(x, y, z) ?? null;
  }

  getFloorHeightBelow(x: number, y: number, z: number): number | null {
    const tile = this.getTile(x, z)?.getFloorHeightBelowTagged(x, y, z);
    return this.resolveFloorY(tile, x, z);
  }

  getFloorHeightBelowTagged(x: number, y: number, z: number): FloorTagged | null {
    return this.getTile(x, z)?.getFloorHeightBelowTagged(x, y, z) ?? null;
  }

  getFloorHeightBelowSmooth(x: number, y: number, z: number): number | null {
    const tile = this.getTile(x, z);
    if (!tile) return this.terrainFallback?.(x, z)?.height ?? null;
    // BVH-side smoothing already does 4-sample bilinear; check whether any
    // neighbour would have been served by the heightmap fallback (no tile or
    // terrain-origin) — if so, defer to the fallback for a continuous result
    // across mixed/terrain seams. Mirrors TiledBvh behavior.
    if (this.shouldUseFallbackForSmoothFloor(tile, x, y, z)) {
      const fb = this.terrainFallback?.(x, z);
      if (fb !== null && fb !== undefined) return fb.height;
    }
    return tile.getFloorHeightBelowSmooth(x, y, z);
  }

  private shouldUseFallbackForSmoothFloor(tile: BvhSpatialGrid, x: number, y: number, z: number): boolean {
    if (this.terrainFallback === null) return false;
    const vs = this.voxelSize;
    const samples: Array<[number, number]> = [
      [x, z],
      [x + vs, z],
      [x, z + vs],
      [x + vs, z + vs],
    ];
    for (const [sx, sz] of samples) {
      const r = tile.getFloorHeightBelowTagged(sx, y, sz);
      if (r === null || r.terrainOrigin) return true;
    }
    return false;
  }

  getFloorHeightWithNormal(x: number, y: number, z: number): VolumeHeightSample | null {
    const tile = this.getTile(x, z);
    const tagged = tile?.getFloorHeightTagged(x, y, z);
    if (tagged && !tagged.terrainOrigin) {
      return tile!.getFloorHeightWithNormal(x, y, z);
    }
    const fb = this.terrainFallback?.(x, z);
    if (fb !== null && fb !== undefined) return fb;
    if (tagged) return tile!.getFloorHeightWithNormal(x, y, z);
    return null;
  }

  forEachLeaf(callback: VolumeLeafCallback): void {
    for (const tile of this.loaded.values()) tile.forEachLeaf(callback);
  }

  segmentHitDistance(
    x0: number, y0: number, z0: number,
    x1: number, y1: number, z1: number,
  ): number | null {
    // Single-tile fast path uses the per-tile BvhSpatialGrid's native BVH raycast.
    const tileA = this.getTile(x0, z0);
    const tileB = this.getTile(x1, z1);
    if (tileA && tileA === tileB) {
      return tileA.segmentHitDistance(x0, y0, z0, x1, y1, z1);
    }
    // Cross-tile (rare during normal play): step over `isSolidAt`. Once
    // volgrid retires this can be replaced with a single BVH-native cast
    // that natively spans tile boundaries.
    return segmentHitBisect(x0, y0, z0, x1, y1, z1, this.voxelSize, (x, y, z) =>
      this.isSolidAt(x, y, z),
    );
  }

  segmentHitWithNormal(
    x0: number, y0: number, z0: number,
    x1: number, y1: number, z1: number,
  ): SegmentHit | null {
    // Single-tile fast path: per-tile BvhSpatialGrid returns the exact
    // triangle face normal.
    const tileA = this.getTile(x0, z0);
    const tileB = this.getTile(x1, z1);
    if (tileA && tileA === tileB) {
      return tileA.segmentHitWithNormal(x0, y0, z0, x1, y1, z1);
    }
    // Cross-tile fallback: use the distance query (which routes via
    // isSolidAt) and approximate an axis-aligned normal. Same trick the
    // volgrid path uses — rare in practice, and projectile dynamics aren't
    // sensitive enough for the slight inaccuracy to matter.
    const t = this.segmentHitDistance(x0, y0, z0, x1, y1, z1);
    if (t === null) return null;
    return {
      t,
      normal: approximateWallNormal(this, x0, y0, z0, x1, y1, z1, t, this.voxelSize),
      isTerrain: false,
    };
  }

  rayCrossesSolid(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): boolean {
    const tileA = this.getTile(x0, z0);
    const tileB = this.getTile(x1, z1);
    if (tileA && tileA === tileB) {
      return tileA.rayCrossesSolid(x0, y0, z0, x1, y1, z1);
    }
    // Cross-tile fallback: step-based. Same shape as `TiledBvh`.
    const dx = x1 - x0;
    const dy = y1 - y0;
    const dz = z1 - z0;
    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (dist < 0.001) return this.isSolidAt(x1, y1, z1);
    const steps = Math.ceil(dist / this.voxelSize);
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      if (this.isSolidAt(x0 + dx * t, y0 + dy * t, z0 + dz * t)) return true;
    }
    return false;
  }
}
